import { ImageOff, Maximize2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { PhotoPreviewDialog } from '@/components/common/PhotoPreviewDialog';
import { MediaPlaceholder } from './MediaPlaceholder';

interface InstitutePhotoProps {
  photoUrl: string | null;
  instituteName: string | null;
  stamp?: ReactNode;
}

export function InstitutePhoto({ photoUrl, instituteName, stamp }: InstitutePhotoProps) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [failedPhotoUrl, setFailedPhotoUrl] = useState<string | null>(null);

  if (!photoUrl || failedPhotoUrl === photoUrl) {
    return (
      <MediaPlaceholder icon={ImageOff}>
        {photoUrl ? 'Photo could not load' : 'No photo'}
      </MediaPlaceholder>
    );
  }

  const title = instituteName ?? 'Institute photo';

  return (
    <>
      <button
        type="button"
        onClick={() => setIsPreviewOpen(true)}
        aria-label="View photo"
        className="group relative block w-full cursor-zoom-in overflow-hidden rounded-lg border focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <img
          src={photoUrl}
          alt={title}
          onError={() => setFailedPhotoUrl(photoUrl)}
          className="aspect-4/3 w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <span className="absolute inset-0 flex items-center justify-center gap-1.5 bg-black/40 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
          <Maximize2 className="size-4" aria-hidden />
          View photo
        </span>
        {stamp && (
          <span className="absolute inset-x-0 bottom-0 bg-linear-to-t from-black/85 to-transparent px-2.5 py-2 text-left text-3xs leading-relaxed text-white/90">
            {stamp}
          </span>
        )}
        <span className="absolute top-2 right-2 rounded-md bg-black/50 p-1 text-white group-hover:opacity-0">
          <Maximize2 className="size-3.5" aria-hidden />
        </span>
      </button>

      <PhotoPreviewDialog
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        photoUrl={photoUrl}
        title={title}
      />
    </>
  );
}
